// frontend/shared-ui/src/components/FileUploader.jsx
import React, { useRef, useState } from "react";
import Button from "./Button";

export default function FileUploader({
  accept = "image/*",
  multiple = false,
  onFiles, // (files: File[]) => void | Promise
  maxFiles = 20,
  maxSizeMB = 10,
  label = "Choose file",
  dragLabel = "Drag & drop or click to browse",
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");

  const validate = (files) => {
    const arr = Array.from(files);
    if (!multiple && arr.length > 1) {
      setError("Only one file allowed");
      return null;
    }
    if (arr.length > maxFiles) {
      setError(`Maximum ${maxFiles} files`);
      return null;
    }
    for (const f of arr) {
      if (maxSizeMB && f.size > maxSizeMB * 1024 * 1024) {
        setError(`File too large: ${f.name} (max ${maxSizeMB} MB)`);
        return null;
      }
    }
    setError("");
    return arr;
  };

  const handle = async (files) => {
    const valid = validate(files);
    if (!valid) return;
    await onFiles?.(valid);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    await handle(e.dataTransfer.files);
  };

  return (
    <div
      className={`fh-uploader ${dragOver ? "fh-uploader--over" : ""} ${disabled ? "fh-uploader--disabled" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => !disabled && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={(e) => handle(e.target.files)}
        disabled={disabled}
      />

      <div className="fh-uploader__icon">
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </div>

      <div className="fh-uploader__text">{dragLabel}</div>
      <div className="fh-uploader__hint">
        {multiple ? `Up to ${maxFiles} files` : "Single file"} · max {maxSizeMB}{" "}
        MB
      </div>

      <div className="mt-2">
        <Button variant="outline" size="sm" disabled={disabled}>
          {label}
        </Button>
      </div>

      {error && <div className="fh-uploader__error">{error}</div>}
    </div>
  );
}
